import React from "react";
import { Drawer, Space, Table, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { packsFor } from "functions/packaging";

const { Text } = Typography;

/**
 * A round as the scanner will walk it: per order, in cart order, the lines in walking order with
 * vak, product, weight, quantity (and in how many packages) and the code that counts them down. Straight from the wave
 * document - what is shown here is exactly what the app gets.
 */
const WaveDetailsDrawer = ({ wave, projection, onClose }) => {
  const { t } = useTranslation();
  if (!wave) return null;

  const lineColumns = [
    { title: "#", dataIndex: "sequence", width: 40 },
    {
      title: t("orderpicking.waves.vak"),
      dataIndex: "location",
      width: 70,
      render: (location, line) =>
        location ? <strong style={{ fontFamily: "monospace" }}>{location}</strong> : <Tag color="orange">{line.offRoute ? t("orderpicking.waves.off_route") : t("orderpicking.waves.no_code")}</Tag>,
    },
    {
      title: t("orderpicking.waves.product"),
      dataIndex: "label",
      render: (label, line) => (
        <div>
          {label}
          {!!line.sub && <Text type="secondary"> · {line.sub}</Text>}
          {!!line.pickNote && <div style={{ fontSize: 12, color: "#d46b08" }}>{line.pickNote}</div>}
        </div>
      ),
    },
    {
      title: t("orderpicking.waves.quantity"),
      dataIndex: "quantity",
      width: 100,
      render: (quantity, line) => {
        const { size, packs, loose } = packsFor(quantity, line.piecesPerPack);
        if (size === 1) return quantity;
        return (
          <div>
            {quantity}
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              {packs > 0 && t("orderpicking.waves.quantity_packs", { packs, size })}
              {packs > 0 && loose > 0 && " + "}
              {loose > 0 && t("orderpicking.waves.quantity_loose", { count: loose })}
            </div>
          </div>
        );
      },
    },
    {
      title: t("orderpicking.waves.progress"),
      dataIndex: "articleCode",
      width: 90,
      render: (code, line, index, orderState) => {
        const state = orderState && orderState.lines ? orderState.lines[code] : null;
        return state ? `${state.picked}/${line.quantity}` : "-";
      },
    },
  ];
  const columnsFor = (orderState) =>
    lineColumns.map((column) =>
      column.dataIndex !== "articleCode" ? column : { ...column, render: (code, line, index) => column.render(code, line, index, orderState) },
    );

  return (
    <Drawer open={!!wave} onClose={onClose} width={760} title={t("orderpicking.waves.details_title", { number: wave.waveNumber, day: wave.pickDay })}>
      {(wave.orders || []).map((order) => {
        const orderState = projection && projection.orders ? projection.orders[order.orderRef] : null;
        return (
        <div key={order.orderRef} style={{ marginBottom: 24 }}>
          <Space size={6} wrap style={{ marginBottom: 6 }}>
            <strong>
              {order.sequence}. {order.orderId}
            </strong>
            <Tag color={order.shopSource === "VCE" ? "green" : "purple"}>{order.shopSource}</Tag>
            <Tag color="blue">{order.boxAdvice}</Tag>
            {order.deliveryType === "pickup" && <Tag color="orange">{t("orderpicking.waves.pickup")}</Tag>}
            {order.linesWithoutWeight > 0 && <Tag color="gold">{t("orderpicking.waves.no_weight", { count: order.linesWithoutWeight })}</Tag>}
            {!!orderState && <Tag color="geekblue">{t(`order.picking_${orderState.status}`)}</Tag>}
            {!!orderState &&
              orderState.exceptions.map((exception, index) => (
                <Tag key={index} color="red">
                  {t(`order.picking_exception_${exception.type}`, { location: exception.location || "-", missing: exception.missing ?? "-" })}
                </Tag>
              ))}
          </Space>
          <Table
            size="small"
            rowKey="articleCode"
            pagination={false}
            columns={columnsFor(orderState)}
            dataSource={order.lines}
          />
        </div>
        );
      })}
    </Drawer>
  );
};

export default WaveDetailsDrawer;
